import React, { useState } from 'react';
import { Home, Menu, X, User, LogOut, Search, ChevronDown, PlusCircle, Heart, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import LanguageSelector from '../common/LanguageSelector';
import { useLanguage } from '../../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout, isLoading, isSyncingProfile } = useAuth();
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    handleNavigation('/');
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-white/95 backdrop-blur-md shadow-soft sticky top-0 z-50 border-b border-neutral-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={() => handleNavigation('/')}
            >
              <img
                src="/stanorama-logo.svg"
                alt="Stanorama"
                className="h-10 w-auto"
              />
            </div>
            <div className="hidden md:ml-8 md:flex md:space-x-1">
              <button
                onClick={() => handleNavigation('/')}
                className={`inline-flex items-center h-10 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive('/') 
                    ? 'bg-primary-50 text-primary-700 shadow-soft' 
                    : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
              >
                {t('common.home')}
              </button>
              <button
                onClick={() => handleNavigation('/search')}
                className={`inline-flex items-center h-10 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive('/search') 
                    ? 'bg-primary-50 text-primary-700 shadow-soft' 
                    : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
              >
                <Search size={16} className="mr-2" />
                {t('common.search')}
              </button>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-3">
            {(isLoading || isSyncingProfile) && (
              <div className="mr-2">
                <LoadingSpinner size="sm" />
              </div>
            )}
            <LanguageSelector />
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => handleNavigation('/buy-tokens')}
                  className="inline-flex items-center h-10 px-4 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium shadow-medium hover:shadow-large transition-all duration-200"
                >
                  <span className="mr-2 font-bold text-lg">₮</span>
                  <span>{user?.tokens}</span>
                  <span className="ml-1 hidden lg:inline">{t('common.tokens')}</span>
                </button>
                <button
                  onClick={() => handleNavigation('/add-property')}
                  className="inline-flex items-center h-10 px-4 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium hover:shadow-large shadow-medium transition-all duration-200"
                >
                  <PlusCircle size={16} className="mr-2" />
                  <span className="hidden lg:inline">{t('common.addProperty')}</span>
                  <span className="lg:hidden">Add</span>
                </button>
                <div className="relative">
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center h-10 max-w-xs text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-neutral-50 hover:bg-neutral-100 px-3 border border-neutral-200 transition-all duration-200"
                    id="user-menu-button"
                    aria-expanded={isUserMenuOpen}
                    aria-haspopup="true"
                  >
                    <div className="w-8 h-8 rounded-lg bg-brand-dark flex items-center justify-center mr-3">
                      <span className="text-white font-medium text-sm">
                        {user?.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium text-neutral-700 hidden lg:block">{user?.name}</span>
                    <ChevronDown size={16} className="ml-2 text-neutral-500" />
                  </button>
                  {isUserMenuOpen && (
                    <div
                      className="origin-top-right absolute right-0 mt-2 w-56 rounded-2xl shadow-large py-2 bg-white ring-1 ring-neutral-200 focus:outline-none backdrop-blur-md"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu-button"
                    >
                      <button
                        onClick={() => handleNavigation('/profile')}
                        className="flex items-center px-4 py-3 text-sm text-neutral-700 w-full text-left hover:bg-neutral-50 transition-colors duration-150"
                        role="menuitem"
                      >
                        <User size={18} className="mr-3 text-neutral-400" /> 
                        {t('common.profile')}
                      </button>
                      <button
                        onClick={() => handleNavigation('/buy-tokens')}
                        className="flex items-center px-4 py-3 text-sm text-neutral-700 w-full text-left hover:bg-neutral-50 transition-colors duration-150"
                        role="menuitem"
                      >
                        <span className="mr-3 inline-flex items-center justify-center w-5 h-5 text-accent-600 font-bold">₮</span> 
                        {t('common.buyTokens')}
                      </button>
                      <button
                        onClick={() => handleNavigation('/my-properties')}
                        className="flex items-center px-4 py-3 text-sm text-neutral-700 w-full text-left hover:bg-neutral-50 transition-colors duration-150"
                        role="menuitem"
                      >
                        <Home size={18} className="mr-3 text-neutral-400" />
                        {t('common.myProperties')}
                      </button>
                      <button
                        onClick={() => handleNavigation('/favorites')}
                        className="flex items-center px-4 py-3 text-sm text-neutral-700 w-full text-left hover:bg-neutral-50 transition-colors duration-150"
                        role="menuitem"
                      >
                        <Heart size={18} className="mr-3 text-neutral-400" />
                        {t('profile.favorites')}
                      </button>
                      <button
                        onClick={() => handleNavigation('/purchased-contacts')}
                        className="flex items-center px-4 py-3 text-sm text-neutral-700 w-full text-left hover:bg-neutral-50 transition-colors duration-150"
                        role="menuitem"
                      >
                        <ShoppingBag size={18} className="mr-3 text-neutral-400" />
                        {t('profile.purchasedContacts')}
                      </button>
                      <hr className="my-2 border-neutral-200" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center px-4 py-3 text-sm text-red-600 w-full text-left hover:bg-red-50 transition-colors duration-150"
                        role="menuitem"
                      >
                        <LogOut size={18} className="mr-3" /> 
                        {t('common.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={() => handleNavigation('/login')}
                  className="h-10 px-4 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200"
                >
                  {t('common.signIn')}
                </button>
                <button
                  onClick={() => handleNavigation('/register')}
                  className="h-10 px-4 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 hover:shadow-large shadow-medium rounded-lg transition-all duration-200"
                >
                  {t('common.signUp')}
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center md:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 focus:outline-none focus:bg-neutral-100 transition-colors duration-200"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">{isMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
              {isMenuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-neutral-200 bg-white/95 backdrop-blur-md">
          <div className="px-4 pt-2 pb-3 space-y-1">
            <div className="py-2">
              <LanguageSelector />
            </div>
            {isAuthenticated && (
              <div className="space-y-2 py-2">
                <button
                  onClick={() => handleNavigation('/buy-tokens')}
                  className="w-full flex items-center justify-center px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-medium shadow-medium"
                >
                  <span className="mr-2 font-bold">₮</span>
                  {user?.tokens} {t('common.tokens')}
                </button>
                <button
                  onClick={() => handleNavigation('/add-property')}
                  className="w-full flex items-center justify-center px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-medium shadow-medium"
                >
                  <PlusCircle size={16} className="mr-2" />
                  {t('common.addProperty')}
                </button>
              </div>
            )}
            
            <div className="space-y-1 py-2">
              <button
                onClick={() => handleNavigation('/')}
                className={`block w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                }`}
              >
                {t('common.home')}
              </button>
              <button
                onClick={() => handleNavigation('/search')}
                className={`flex items-center w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/search')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                }`}
              >
                <Search size={18} className="mr-3" /> {t('common.search')}
              </button>
            </div>
          </div>
          
          {isAuthenticated ? (
            <div className="pt-4 pb-3 border-t border-neutral-200">
              <div className="flex items-center px-4 mb-3">
                <div className="w-10 h-10 rounded-xl bg-brand-dark flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user?.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-neutral-800">{user?.name}</div>
                  <div className="text-sm font-medium text-neutral-500">{user?.email}</div>
                </div>
              </div>
              <div className="space-y-1 px-4">
                <button
                  onClick={() => handleNavigation('/profile')}
                  className="flex items-center w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 rounded-xl transition-colors duration-200"
                >
                  <User size={18} className="mr-3" /> {t('common.profile')}
                </button>
                <button
                  onClick={() => handleNavigation('/my-properties')}
                  className="flex items-center w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 rounded-xl transition-colors duration-200"
                >
                  <Home size={18} className="mr-3" />
                  {t('common.myProperties')}
                </button>
                <button
                  onClick={() => handleNavigation('/favorites')}
                  className="flex items-center w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 rounded-xl transition-colors duration-200"
                >
                  <Heart size={18} className="mr-3" />
                  {t('profile.favorites')}
                </button>
                <button
                  onClick={() => handleNavigation('/purchased-contacts')}
                  className="flex items-center w-full text-left px-4 py-3 text-base font-medium text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 rounded-xl transition-colors duration-200"
                >
                  <ShoppingBag size={18} className="mr-3" />
                  {t('profile.purchasedContacts')}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full text-left px-4 py-3 text-base font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-colors duration-200"
                >
                  <LogOut size={18} className="mr-3" /> {t('common.logout')}
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-4 pb-3 border-t border-neutral-200">
              <div className="flex flex-col space-y-2 px-4">
                <button
                  onClick={() => handleNavigation('/login')}
                  className="w-full px-4 py-3 text-center font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors duration-200"
                >
                  {t('common.signIn')}
                </button>
                <button
                  onClick={() => handleNavigation('/register')}
                  className="w-full px-4 py-3 text-center font-medium text-white bg-primary-500 hover:bg-primary-600 shadow-medium rounded-xl transition-all duration-200"
                >
                  {t('common.signUp')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
